import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import LoadingWrap from "../../component/LoadingWrap.tsx";

const WEBHOOK_STATUS_ORIGIN = "https://webhook.phototime.works";
const WEBHOOK_STATUS_PATH = "/api/v1/sse/status";

function getEnv(key: string): string | undefined {
  const p = (globalThis as any)?.process;
  return p?.env?.[key] as string | undefined;
}

function buildStatusLookupUrl(orderId: string): string {
  const q = encodeURIComponent(orderId);
  return `${WEBHOOK_STATUS_ORIGIN}${WEBHOOK_STATUS_PATH}?orderId=${q}`;
}

function statusFetchHeaders(): HeadersInit {
  const headers: Record<string, string> = {
    Accept: "application/json",
    "X-Custom-Header":
      getEnv("REACT_APP_WEBHOOK_STATUS_SECRET") ??
      "your-secret-header-value-here",
  };
  const apiKey = getEnv("REACT_APP_WEBHOOK_X_API_KEY");
  if (apiKey) {
    headers["X-API-Key"] = apiKey;
  }
  return headers;
}

export type TransactionStatusResponse = {
  message: string;
  orderId: string;
  paymentStatus: string | null;
  sseConnected: boolean;
  data?: {
    amount?: number;
    remark?: string;
    status?: string;
    vaName?: string;
  };
};

type UiKind = "success" | "waiting" | "cancelled" | "unknown";

function mapPayloadToUi(p: TransactionStatusResponse): {
  kind: UiKind;
  title: string;
} {
  const pay = (p.paymentStatus ?? p.data?.status ?? "").toString().toUpperCase();
  if (pay === "SUCCESS" || pay.includes("SUCCESS")) {
    return { kind: "success", title: "Thành công" };
  }
  if (p.sseConnected === true) {
    return { kind: "waiting", title: "Đang chờ kết nối nhận tiền" };
  }
  const m = (p.message || "").toLowerCase();
  if (
    m.includes("disconnected") ||
    m.includes("no payment record") ||
    m.includes("reconnect")
  ) {
    return { kind: "cancelled", title: "Đã hủy kết nối" };
  }
  return { kind: "unknown", title: "Chưa xác định" };
}

function formatPaymentStatus(value: unknown): string | null {
  if (value === undefined || value === null) return null;
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return null;
    }
  }
  return null;
}

function normalizeStatusPayload(
  raw: unknown,
  fallbackOrderId: string
): TransactionStatusResponse {
  if (!raw || typeof raw !== "object") {
    return {
      message: "Phản hồi không đúng định dạng.",
      orderId: fallbackOrderId,
      paymentStatus: null,
      sseConnected: false,
      data: undefined,
    };
  }
  const o = raw as Record<string, unknown>;
  const dataRaw =
    o.data && typeof o.data === "object" ? (o.data as Record<string, unknown>) : null;
  return {
    message: typeof o.message === "string" ? o.message : "",
    orderId:
      typeof o.orderId === "string" && o.orderId.length > 0
        ? o.orderId
        : fallbackOrderId,
    paymentStatus: formatPaymentStatus(o.paymentStatus),
    sseConnected: o.sseConnected === true,
    data: dataRaw
      ? {
          amount: typeof dataRaw.amount === "number" ? dataRaw.amount : undefined,
          remark: typeof dataRaw.remark === "string" ? dataRaw.remark : undefined,
          status: typeof dataRaw.status === "string" ? dataRaw.status : undefined,
          vaName: typeof dataRaw.vaName === "string" ? dataRaw.vaName : undefined,
        }
      : undefined,
  };
}

const StatusTransactionPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const paramOrderId = searchParams.get("orderId")?.trim() ?? "";

  const [orderIdInput, setOrderIdInput] = useState(paramOrderId);
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState<TransactionStatusResponse | null>(
    null
  );

  const notify = (msg: string) => toast(msg);

  const runLookup = useCallback(async (id: string) => {
    const trimmed = id.trim();
    if (!trimmed) {
      notify("Vui lòng nhập Order ID");
      return;
    }
    setLoading(true);
    setPayload(null);
    try {
      const res = await fetch(buildStatusLookupUrl(trimmed), {
        method: "GET",
        headers: statusFetchHeaders(),
      });
      const resJson = await res.json();
      setPayload(normalizeStatusPayload(resJson, trimmed));
      if (!res.ok) {
        const msg =
          resJson &&
          typeof resJson === "object" &&
          typeof (resJson as { message?: unknown }).message === "string"
            ? (resJson as { message: string }).message
            : `Lỗi HTTP ${res.status}`;
        notify(msg);
      }
    } catch (e) {
      console.log("=====status transaction=====", e);
      const networkFail =
        e instanceof TypeError &&
        String(e.message).toLowerCase().includes("fetch");
      notify(
        networkFail
          ? "Lỗi mạng hoặc CORS — webhook cần cho phép origin và header từ browser."
          : "Đã có lỗi xảy ra, vui lòng thử lại sau"
      );
      setPayload({
        message: networkFail
          ? "Không gọi được API (CORS/mạng). Kiểm tra X-API-Key / secret và CORS trên webhook."
          : "Đã có lỗi xảy ra, vui lòng thử lại sau",
        orderId: trimmed,
        paymentStatus: null,
        sseConnected: false,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setOrderIdInput(paramOrderId);
    if (!paramOrderId) {
      setPayload(null);
      return;
    }
    runLookup(paramOrderId);
  }, [paramOrderId, runLookup]);

  useEffect(() => {
    if (payload && !loading) {
      document
        .getElementById("status-transaction-result")
        ?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [payload, loading]);

  const submitLookup = (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) {
      notify("Vui lòng nhập Order ID");
      return;
    }
    if (trimmed === paramOrderId) runLookup(trimmed);
    else setSearchParams({ orderId: trimmed }, { replace: true });
  };

  const clearResult = () => {
    setPayload(null);
    setSearchParams({}, { replace: true });
  };

  const ui = payload ? mapPayloadToUi(payload) : null;

  const statusBadgeClass =
    ui?.kind === "success"
      ? "text-emerald-700 bg-white border-emerald-500"
      : ui?.kind === "waiting"
        ? "text-amber-800 bg-white border-amber-500"
        : ui?.kind === "cancelled"
          ? "text-rose-800 bg-white border-rose-500"
          : "text-slate-700 bg-white border-slate-400";

  return (
    <LoadingWrap active={loading} >
      <div className="flex flex-col min-h-[calc(100vh-80px)] px-4 pb-10 bg-white">
        <h1 className="mt-10 text-4xl sm:text-5xl font-semibold text-center text-primary">
          STATUS TRANSACTION
        </h1>

        <div className="w-full max-w-5xl mx-auto mt-8 px-0 sm:px-2">
          <p className="font-semibold text-primary">Order ID</p>
          <input
            type="text"
            placeholder="Nhập orderId…"
            value={orderIdInput}
            onChange={(e) => setOrderIdInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") submitLookup(orderIdInput);
            }}
            className="w-full mt-2 px-5 py-3 rounded-xl border border-primary outline-none text-primary placeholder:text-pink-300"
          />
        </div>

        <div className="flex justify-center mt-8">
          <button
            type="button"
            onClick={() => submitLookup(orderIdInput)}
            className="px-10 py-2 text-white rounded-full bg-primary"
          >
            Tra cứu
          </button>
        </div>

        <div className="flex-1 flex flex-col min-h-0 justify-start mt-14 sm:mt-16 w-full max-w-3xl mx-auto">
          {payload && !loading && ui && (
            <div
              id="status-transaction-result"
              className="w-full border-2 border-primary/30 rounded-xl shadow-md bg-white overflow-hidden scroll-mt-4"
            >
              <div className="px-4 py-2.5 border-b border-pink-100 bg-pink-50/60 flex items-center justify-between gap-2">
                <p className="font-bold text-primary text-sm sm:text-base">
                  Kết quả tra cứu
                </p>
                <button
                  type="button"
                  onClick={clearResult}
                  className="shrink-0 text-xs px-3 py-1.5 rounded-full bg-red-500 text-white hover:bg-red-600 font-semibold"
                >
                  Đóng
                </button>
              </div>

              <div className="p-4 sm:p-5 bg-pink-50/70">
                <div className="flex flex-col sm:flex-row justify-stretch gap-3 sm:min-h-[240px]">
                  <div className="bg-white rounded-2xl px-2 py-2 w-full sm:w-[38%] flex justify-center min-h-[140px] sm:min-h-0 border border-pink-200 shadow-sm">
                    <div className="flex-1 w-full px-4 py-5 flex flex-col justify-center text-center border-2 border-pink-200 rounded-2xl min-w-0 bg-pink-100 shadow-[0_12px_28px_rgba(236,72,153,0.18)]">
                      <p className="text-[#AD287D] font-extrabold text-xs uppercase tracking-wide">
                        Trạng thái
                      </p>
                      <p
                        className={`mt-3 text-base font-extrabold border-2 rounded-full px-4 py-1.5 inline-block self-center ${statusBadgeClass}`}
                      >
                        {ui.title}
                      </p>
                    </div>
                  </div>

                  <div className="bg-white rounded-2xl px-2 py-2 w-full sm:flex-1 flex min-h-[170px] sm:min-h-0 border border-pink-200 shadow-sm">
                    <div className="px-4 py-4 border border-pink-100 rounded-2xl w-full min-h-0 overflow-y-auto text-left flex flex-col justify-center">
                      <p className="font-bold text-primary text-base whitespace-pre-wrap leading-relaxed">
                        {payload.message}
                      </p>
                      <div className="mt-5 space-y-2.5">
                      <p className="text-[13px] text-slate-800 leading-relaxed">
                        <span className="font-semibold">Trạng thái thanh toán:</span>{" "}
                        <span className="font-mono font-extrabold text-slate-900">
                          {payload.paymentStatus === null
                            ? "null"
                            : JSON.stringify(payload.paymentStatus)}
                        </span>
                      </p>
                      {typeof payload.data?.amount === "number" && (
                        <p className="text-[13px] text-slate-800 leading-relaxed">
                          <span className="font-semibold">Số tiền:</span>{" "}
                          <span className="font-mono font-extrabold text-amber-900 bg-amber-200/90 px-1.5 py-0.5 rounded-md">
                            {payload.data.amount}
                          </span>
                        </p>
                      )}
                      {/* {payload.data?.vaName && (
                        <p className="text-[12px] text-white/90 leading-relaxed">
                          Mã thanh toán:{" "}
                          <span className="font-mono font-extrabold text-indigo-900 bg-indigo-200/90 px-1.5 py-0.5 rounded-md">
                            {payload.data.vaName}
                          </span>
                        </p>
                      )} */}
                      {payload.data?.remark && (
                        <p className="text-[13px] text-slate-800 leading-relaxed">
                          <span className="font-semibold">Nội dung chuyển khoản:</span>{" "}
                          <span className="font-mono font-semibold text-emerald-900 bg-emerald-200/90 px-1.5 py-0.5 rounded-md">
                            {payload.data.remark}
                          </span>
                        </p>
                      )}
                      <p className="pt-1 text-[13px] text-[#AD287D] font-extrabold break-all leading-relaxed">
                        <span className="font-semibold text-slate-800">Mã đơn hàng:</span>{" "}
                        <span className="font-mono">{payload.orderId}</span>
                      </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <ToastContainer />
      </div>
    </LoadingWrap>
  );
};

export default StatusTransactionPage;
